import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { api } from "../../lib/gameApi";
import { ensureAnonAuth } from "../../firebase";
import {
  Body,
  Input,
  ButtonFilled,
  Header,
} from "../../styleguide/atoms/index.js";
import * as styles from "../../styleguide/styles/Stylesheet.js";
import FluidBackground from "./FluidBackground";
import Rules from "./components/Rules.js";
import { CharacterCard } from "./components/CharacterCard";
import { renderRichText } from "../../lib/renderRichText";
import { getCharactersForLobby } from "../../lib/getCharactersForLobby";
import Footer from "../Footer.js";
import GameLogo from "../../media/images/summer-camp-logo.png";

const PageContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
`;

const JumpLink = styled.div`
  text-align: center;
  font-family: ${styles.font_sans};
  font-size: 14px;
  font-weight: ${styles.font_bold};
  color: ${styles.color_white};
  line-height: 1.4;
  border-radius: 5px;
  padding: 8px 12px;
  text-transform: uppercase;
  border: 2px solid ${styles.color_white};
  margin: ${styles.spacing_standard} 0 0;
  cursor: pointer;
  transition: 250ms all;

  &&:hover {
    color: ${styles.color_accent};
    background-color: rgba(255, 255, 255, 1);
  }
`;

const IntroWrapper = styled.div`
  width: 100%;
  min-width: 560px;
  max-width: ${styles.width_sm};
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${styles.spacing_standard_half};
  padding: ${styles.spacing_standard_large} ${styles.spacing_standard};

  @media only screen and (max-width: ${styles.width_md}) {
    min-width: unset;
  }
`;

const LogoContainer = styled.img`
  width: 100%;
  max-width: 680px;
  margin: 0 0 ${styles.spacing_standard};
`;

const JoinWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${styles.spacing_standard_half};
`;

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const RulesContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${styles.color_white};
`;

const CharactersList = styled.div`
  width: 100%;
  max-width: 1600px;
  padding: 0 ${styles.spacing_standard} ${styles.spacing_standard_half};
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: ${styles.spacing_standard};
`;

export default function CampHome() {
  const [code, setCode] = useState("");
  const [creating, setcreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [roles, setRoles] = useState([]);
  const history = useHistory();
  const rulesRef = useRef(null);

  // contentful once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const playable = await getCharactersForLobby({ playableOnly: true });
      if (!cancelled) setRoles(playable);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToRules = () => {
    if (rulesRef.current) {
      rulesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  async function handleCreate() {
    setcreating(true);
    await ensureAnonAuth();
    const { gameId } = await api.createGame();
    history.push(`/lobby/${gameId}`);
  }

  async function handleJoin() {
    setJoining(true);
    setJoinError("");
    try {
      await ensureAnonAuth();
      const { gameId } = await api.joinGame(code.trim().toUpperCase());
      history.push(`/lobby/${gameId}`);
    } catch (err) {
      const friendly =
        err?.code === "functions/not-found" ||
        /Game not found/i.test(err?.message || "")
          ? "Game not found."
          : "Could not join game.";
      setJoinError(friendly);
    } finally {
      setJoining(false);
    }
  }

  return (
    <PageContainer>
      <FluidBackground>
        <IntroWrapper>
          <LogoContainer src={GameLogo} />
          <Body align="center" color={styles.color_white}>
            <p>
              <b>A multiplayer social deduction game.</b>
            </p>
          </Body>
          <ButtonWrapper>
            <ButtonFilled
              disabled={creating}
              text={creating ? "Creating" : "Create a Lobby"}
              onClick={handleCreate}
              color="accent"
            />
          </ButtonWrapper>
          <JoinWrapper>
            <Input
              id="enter-code"
              placeholder="Enter code"
              value={code}
              uppercase
              disabled={joining}
              maxLength={6}
              onChange={(e) => {
                setCode(e.target.value);
                if (joinError) setJoinError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (code && !joining) handleJoin();
                }
              }}
            />
            <ButtonWrapper>
              <ButtonFilled
                disabled={!code || joining}
                text={joining ? "Joining" : "Join Lobby"}
                onClick={handleJoin}
                color="accent"
              />
            </ButtonWrapper>
          </JoinWrapper>
          {joinError ? (
            <Body align="center" color={styles.color_red}>
              {joinError}
            </Body>
          ) : null}
          <JumpLink
            role="button"
            tabIndex={0}
            onClick={scrollToRules}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                scrollToRules();
              }
            }}
            aria-label="Jump to rules section"
          >
            How to play
          </JumpLink>
        </IntroWrapper>
      </FluidBackground>
      <RulesContainer ref={rulesRef}>
        <Rules createAGame></Rules>
        <Header
          type="h3"
          align="center"
          padding={`0 0 ${styles.spacing_standard_large}`}
        >
          Roles
        </Header>
        <CharactersList>
          {roles.map((r) => {
            const descriptionNodes = r.descriptionRich ? (
              renderRichText(r.descriptionRich)
            ) : r.description ? (
              <>{r.description}</>
            ) : null;
            return (
              <CharacterCard
                key={r.id}
                image={r.imageUrl}
                description={descriptionNodes}
                role={r.team}
                title={r.title}
              />
            );
          })}
          <Header type="h3" align="center" padding="52px 0">
            More Roles Coming Soon!
          </Header>
        </CharactersList>
      </RulesContainer>
      <Footer />
    </PageContainer>
  );
}
